import { Color, Style } from "./alert-variants.ts";
import { Root, createRoot } from "react-dom/client";
import { Alert } from "./alert.tsx";

interface PopupAlertProps {
  message: string;
  color: Color;
  style: Style;
  title?: string;
}

let alertReactRoot: Root | null = null;
let rootElement: HTMLElement | null = null;
let destroyTimeout: number | null = null;

const destroyRoot = () => {
  alertReactRoot?.unmount();
  rootElement?.remove();
  alertReactRoot = null;
  rootElement = null;
};

const scheduleDestroy = (ms: number) => {
  window.clearTimeout(destroyTimeout ?? undefined);
  destroyTimeout = window.setTimeout(destroyRoot, ms);
};

const createRootIfNotExist = (className: string) => {
  if (!alertReactRoot) {
    rootElement = document.createElement("div");
    rootElement.id = "alert-root";
    document.body.appendChild(rootElement);
    alertReactRoot = createRoot(rootElement);
  }
  if (rootElement) rootElement.className = className;
  return alertReactRoot;
};

export const showPopupAlertFromLocalStorage = () => {
  const alertJson: string | null = window.sessionStorage.getItem("alert");

  // only show alert if there's something to show
  if (!alertJson) {
    return;
  }
  window.sessionStorage.removeItem("alert");
  const alert = JSON.parse(alertJson) as PopupAlertProps;
  // add a root at the end of the body
  const reactRoot = createRootIfNotExist("twp absolute left-1/2 top-[10%] z-[1000] -translate-x-1/2");
  reactRoot.render(
    <Alert
      color={alert.color}
      style={alert.style}
      message={alert.message}
      title={alert.title}
      dismissible
      onDismiss={destroyRoot}
      renderLeadingIcon={() => <i className="fa-sharp fa-regular fa-shield-check" />}
    />,
  );
  scheduleDestroy(5000);
};

export const setPopupAlertInLocalStorage = (alert: PopupAlertProps) => {
  window.sessionStorage.setItem("alert", JSON.stringify(alert));
};

export const showErrorMessage = (title: string, message: string) => {
  const reactRoot = createRootIfNotExist(
    "twp fixed left-1/2 bottom-[20px] md:bottom-[40px] z-[99999] -translate-x-1/2",
  );
  reactRoot.render(
    <Alert
      color={"red"}
      style="outlined"
      title={title}
      message={<span className="whitespace-nowrap">{message}</span>}
      dismissible
      onDismiss={destroyRoot}
    />,
  );
  scheduleDestroy(15000);
};

export const showSuccessMessage = (message: string) => {
  const reactRoot = createRootIfNotExist("twp absolute left-1/2 top-[10%] z-[99999] -translate-x-1/2");
  reactRoot.render(
    <Alert
      color={"green"}
      style={"solid"}
      message={message}
      dismissible
      onDismiss={destroyRoot}
      renderLeadingIcon={() => <i className="fa-sharp fa-regular fa-shield-check" />}
    />,
  );
  scheduleDestroy(5000);
};
